/**
 * @file Allow using app feature flags
 */
import { hasWwDebugUrlParam } from '@@/bits/dev_url_params'
import environment from '@@/bits/environment'
import window from '@@/bits/global'

type CurrentFlags =
  | 'accessControlSettingsV2'
  | 'accessControlSettingsV2RetroactiveUpdate'
  | 'activityPanelV2'
  | 'addPostLink'
  | 'affiliateSignupLink'
  | 'aiGrading'
  | 'aiImageSearch'
  | 'aiPermissionsForAiRecipes'
  | 'aiPermissionsForImageAndSpeechGeneration'
  | 'aiSuggestionsOnMakePage'
  | 'allSlideAsImageWithBanksy'
  | 'arLensesPreview'
  | 'attachmentPreviewWidthAdjustmentForGridFormat'
  | 'autoContentModeration'
  | 'banksy'
  | 'banksyDev'
  | 'billingAddress'
  | 'brahmsCheck'
  | 'campus'
  | 'changeEmailVerification'
  | 'classesV1'
  | 'classInformation'
  | 'classlinkSso'
  | 'classroomLicensesV2'
  | 'classroomPlanPricingExperiment'
  | 'cleverSso'
  | 'collaboratorSearchHideStrangers'
  | 'commandBar'
  | 'contentPickerCombinedAvRecorders'
  | 'commentAutoPublishOnUpload'
  | 'currentSlideAsImageWithBanksy'
  | 'customPostUserProperty'
  | 'customPostVoteAndRatingProperties'
  | 'dashboardAnnouncement'
  | 'dashboardFolderDalfe'
  | 'dashboardUpgradeCta'
  | 'dashHeaderCtaButton'
  | 'debugFrontend'
  | 'defaultDashboardLibraryNavigation'
  | 'delayedReactionSorting'
  | 'demoPadletPanel'
  | 'demoPadletSignup'
  | 'deprecateArsPlans'
  | 'deprecateDrivePage'
  | 'deviceUploads'
  | 'downloadWallAttachmentsV2'
  | 'editThumbnail'
  | 'editThumbnailForAudioVideoFiles'
  | 'embeddedPadletsOnExpandedPost'
  | 'embeddedPadletsOnSlideshow'
  | 'exitTicket'
  | 'fasterDashSettings'
  | 'featuredSection'
  | 'fetchLinksWithWishes'
  | 'frontChat'
  | 'fullClientSurface'
  | 'galleryTemplates'
  | 'goldPlansVisible'
  | 'googleAppLicensing'
  | 'googleClassroom'
  | 'googleClassroomGrading'
  | 'googleWorkspaceRostering'
  | 'grading'
  | 'groupPostsV2'
  | 'hideCommentAttribution'
  | 'higherMonthlyPricingPlans'
  | 'importPage'
  | 'internalNavigateImmediately'
  | 'joypixels8p0'
  | 'libraryGoogleAppLicensing'
  | 'librarySamlLogin'
  | 'librarySettingsConnectionsTab'
  | 'lmsUserGroupSync'
  | 'lottieWebWorker'
  | 'ltiDeepLinkingMenuV2'
  | 'magicPadlet'
  | 'makePageLayoutExistingUsersExperiment'
  | 'makePageLayoutNewTeachersExperiment'
  | 'makePageSimplifiedLayoutExperiment'
  | 'makePageUpgradeModal'
  | 'microsoftOneDrivePicker'
  | 'microsoftTeams'
  | 'mobileDeviceDprOverride'
  | 'newSignupWelcomePage'
  | 'notifyFailedPayments'
  | 'onboardingDemoV2'
  | 'onboardingDemoV2Copy'
  | 'onboardingGridLayout'
  | 'onboardingPanel'
  | 'openInAppBanner'
  | 'padletAnalytics'
  | 'padletPickerV2'
  | 'pdfImportButton'
  | 'pdndSurfaceGrid'
  | 'pdndSurfaceMap'
  | 'pdndSurfaceMatrix'
  | 'personalPricingPlansExperiment'
  | 'personalPricingPlansExperimentV2'
  | 'platinumResellerSelfServe'
  | 'playVideoInExpanded'
  | 'postComposerAttachmentPickerV2'
  | 'posthog'
  | 'pptxImport'
  | 'prefetchSurface'
  | 'publicApi'
  | 'pwaInstallButton'
  | 'reactionsToCustomPropertiesMigration'
  | 'realtimeFetching'
  | 'remakeLink'
  | 'remakeSettingV2'
  | 'removeArchive'
  | 'removePlayMode'
  | 'rostering'
  | 'samlIdp'
  | 'samlSetup'
  | 'sandboxAi'
  | 'sandboxCode'
  | 'sandboxCopyPasteFrames'
  | 'whiteboardPermissionsV2'
  | 'sandboxPngFramePreview'
  | 'sandboxRetainActiveTool'
  | 'sandboxSoundFx'
  | 'sandboxTextFormatting'
  | 'sandboxTextSizeModifier'
  | 'sandboxThemeBackgrounds'
  | 'sandboxVisualFx'
  | 'schoolLibraryFreeze'
  | 'schoolLibraryOnboardingV2'
  | 'schoolLibraryPermissions'
  | 'schoolLibrarySSO'
  | 'scrollSmoothnessTracking'
  | 'selfServeFreeTrial'
  | 'showAnonymousUserNames'
  | 'showCommandPalette'
  | 'showInvitedLibraryUsers'
  | 'signUpSurface'
  | 'slideshowRemote'
  | 'smartSuggestionsExperiment'
  | 'stripeBillingTax'
  | 'stripeBillingTax'
  | 'suggestedTemplates'
  | 'surfaceAiChat' // keep this even though the feature is launched. This is to allow us to force AI Chat to show for some users even though they don't quality for it.
  | 'surfaceCommentsPerPost'
  | 'surfaceCommentVideoPlayer'
  | 'surfaceLibraryOrgVisitorPermissions'
  | 'surfaceMapsV2'
  | 'surfaceRoleBadges'
  | 'surfaceTopbar'
  | 'taxIdOnCheckout'
  | 'templatesWithNewSidebar'
  | 'transferPadletTool'
  | 'transferSchoolPadletPermission'
  | 'translatePadlet'
  | 'translatePadletV2'
  | 'translationReporter'
  | 'undoRedoDeleteActions'
  | 'undoRedoDeleteSectionActions'
  | 'unifiedSandboxColors'
  | 'unlimitedSandboxesV2'
  | 'upgradeModalCopyAExperiment'
  | 'upgradeModalSingleStepExperiment'
  | 'userBeltY2025'
  | 'userBeltY2026'
  | 'videoTrimming'
  | 'virtualFieldTrip'
  | 'virtualizedDashboard'
  | 'virtualizedPostsGrid'
  | 'wallCardMaxWidth'
  | 'wallContentProtection'
  | 'wallEngagementTime'
  | 'wallEngagementTimeTracking'
  | 'whiteboardPreloadApis'
  | 'yodishLanguage'
  | 'zapierIntegration'

function isAppUsing(feature: CurrentFlags, version?: null | number): boolean {
  const ww = window.ww || {}
  const viteAppFlip =
    window.wwStartingState != null && typeof window.wwStartingState === 'function' ? window.wwStartingState().flip : {}
  const flip = ww.flip || ww.vueStartingState?.flip || viteAppFlip || {}
  if (version) {
    return flip[feature] >= version
  }
  return flip[feature] || false
}

export const isDebugMode =
  (environment === 'test' && ['true', 'yes', '1'].includes(process.env.WW_DEBUG ?? '')) ||
  isAppUsing('debugFrontend') ||
  hasWwDebugUrlParam()

if (environment !== 'production' && isAppUsing('debugFrontend')) {
  console.log('Already debugging frontend in dev.') // goodcheck-disable-line
}

// If on padlet.dev, use the local Banksy server to test PDF exports.
// Banksy and Banksy Dev on Cloud Run won't work because they cannot access a padlet.dev padlet.
// You will need to run the Banksy server first before you can test PDF exports.
// See: /services/banksy/README.md
export const isAppUsingBanksyLocalDev = environment === 'development'

export { isAppUsing }
